import { useState, useEffect } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { Smartphone, ExternalLink, Save, Apple, PlayCircle, Bike, Users, RefreshCw, Megaphone, Image, Palette, Type, Plus, Trash2, ChevronDown, ChevronUp, Wrench, Utensils, CheckCircle } from "lucide-react";

// Pre-defined theme options
const THEME_OPTIONS = [
  { id: "green", name: "Fresh Green", color: "#22c55e" },
  { id: "orange", name: "Citrus Orange", color: "#f97316" },
  { id: "purple", name: "Berry Purple", color: "#a855f7" },
  { id: "farmzy_original", name: "Farmzy Original", color: "#4ade80" },
  { id: "custom", name: "Custom URL", color: "#6b7280" },
];

// Theme background URLs
const THEME_BACKGROUNDS = {
  "green": "https://static.prod-images.emergentagent.com/jobs/a0c50b28-25cd-4f8c-bd27-56b37be0bc58/images/2b1e1ab42584510d1f3e69442f0dff5e81f0cb5a509eca8669362a60a759ffaf.png",
  "orange": "https://static.prod-images.emergentagent.com/jobs/a0c50b28-25cd-4f8c-bd27-56b37be0bc58/images/7be3f2ef5b8ac6c79cded92c61b104655806df981b23a8d3ea08b364658eec0b.png",
  "purple": "https://static.prod-images.emergentagent.com/jobs/a0c50b28-25cd-4f8c-bd27-56b37be0bc58/images/bc7be30bde0fdf2e323a01859407d9446d46fc0e5a14ea810528b6fd2ac65c8f.png",
  "farmzy_original": "https://static.prod-images.emergentagent.com/jobs/a0c50b28-25cd-4f8c-bd27-56b37be0bc58/images/adb3d6cecec02de8a8adc8f0bd8e05ee6a35a7f6aaff007aac8b8d9814902735.png"
};

const DEFAULT_BANNER = {
  id: "",
  enabled: true,
  url: "https://example.com",
  theme: "green",
  background: "",
  headline: "YOUR HEADLINE",
  subheadline: "Your Subheadline",
  text_color: "#ffffff",
  title: "Brand Name",
  description: "Short description"
};

const AppSettingsPage = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [appUrls, setAppUrls] = useState({
    playstore_url: "",
    appstore_url: "",
    customer_playstore_url: "",
    customer_appstore_url: ""
  });
  const [adBanners, setAdBanners] = useState([{...DEFAULT_BANNER, id: "default"}]);
  const [expandedBanner, setExpandedBanner] = useState("default");
  const [bannerAnalytics, setBannerAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [appVersions, setAppVersions] = useState({
    rider_app_version: "",
    rider_app_build_number: "",
    customer_app_version: "",
    customer_app_build_number: "",
    force_update_rider: false,
    force_update_customer: false,
    show_update_popup_rider: true,
    show_update_popup_customer: true
  });
  
  // Sync utility state
  const [syncing, setSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState(null);
  const [syncDateFrom, setSyncDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 3);  // Default to 3 days ago for faster processing
    return d.toISOString().split('T')[0];
  });
  const [syncDateTo, setSyncDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  
  // Fix ledger state
  const [fixingLedger, setFixingLedger] = useState(false);
  const [ledgerFixResult, setLedgerFixResult] = useState(null);

  useEffect(() => {
    fetchAppUrls();
    fetchAppVersions();
    fetchAdBanners();
    fetchBannerAnalytics();
  }, []);

  const fetchAppUrls = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/settings/app-urls`);
      setAppUrls({
        playstore_url: res.data.playstore_url || "",
        appstore_url: res.data.appstore_url || "",
        customer_playstore_url: res.data.customer_playstore_url || "",
        customer_appstore_url: res.data.customer_appstore_url || ""
      });
    } catch (err) {
      console.error("Failed to fetch app URLs:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAppVersions = async () => {
    try {
      const res = await axios.get(`${API}/settings/app-versions`);
      setAppVersions({
        rider_app_version: res.data.rider_app_version || "",
        rider_app_build_number: res.data.rider_app_build_number || "",
        customer_app_version: res.data.customer_app_version || "",
        customer_app_build_number: res.data.customer_app_build_number || "",
        force_update_rider: res.data.force_update_rider || false,
        force_update_customer: res.data.force_update_customer || false,
        show_update_popup_rider: res.data.show_update_popup_rider !== false,
        show_update_popup_customer: res.data.show_update_popup_customer !== false
      });
    } catch (err) {
      console.error("Failed to fetch app versions:", err);
    }
  };

  const fetchAdBanners = async () => {
    try {
      const res = await axios.get(`${API}/settings/ad-banners`);
      if (res.data.banners && res.data.banners.length > 0) {
        setAdBanners(res.data.banners);
        setExpandedBanner(res.data.banners[0].id);
      }
    } catch (err) {
      console.error("Failed to fetch ad banners:", err);
    }
  };

  const fetchBannerAnalytics = async () => {
    if (!user?.id) return;
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/settings/ad-banners/analytics?admin_id=${user.id}&days=30`);
      setBannerAnalytics(res.data);
    } catch (err) {
      console.error("Failed to fetch banner analytics:", err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      console.log("Saving app URLs with user.id:", user?.id);
      console.log("App URLs to save:", appUrls);
      await axios.put(`${API}/settings/admin/app-urls?admin_id=${user.id}`, appUrls);
      toast.success("App URLs updated successfully");
    } catch (err) {
      console.error("Save error:", err.response?.data || err.message);
      toast.error(err.response?.data?.detail || "Failed to update app URLs");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveVersions = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/admin/app-versions?admin_id=${user.id}`, appVersions);
      toast.success("App versions updated successfully");
    } catch (err) {
      console.error("Save error:", err.response?.data || err.message);
      toast.error(err.response?.data?.detail || "Failed to update app versions");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAdBanners = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/admin/ad-banners?admin_id=${user.id}`, { banners: adBanners });
      toast.success("Ad banners updated successfully");
    } catch (err) {
      console.error("Save error:", err.response?.data || err.message);
      toast.error(err.response?.data?.detail || "Failed to update ad banners");
    } finally {
      setSaving(false);
    }
  };

  const addNewBanner = () => {
    const newBanner = {
      ...DEFAULT_BANNER,
      id: `banner_${Date.now()}`,
      headline: "NEW BANNER",
      title: "New Banner"
    };
    setAdBanners([...adBanners, newBanner]);
    setExpandedBanner(newBanner.id);
  };

  const removeBanner = (id) => {
    if (adBanners.length <= 1) {
      toast.error("You must have at least one banner");
      return;
    }
    setAdBanners(adBanners.filter(b => b.id !== id));
    if (expandedBanner === id) {
      setExpandedBanner(adBanners[0].id);
    }
  };

  const updateBanner = (id, field, value) => {
    setAdBanners(adBanners.map(b => 
      b.id === id ? { ...b, [field]: value } : b
    ));
  };

  const getBannerBackground = (banner) => {
    if (banner.theme === "custom" && banner.background) {
      return banner.background;
    }
    return THEME_BACKGROUNDS[banner.theme] || THEME_BACKGROUNDS.green;
  };

  // Sync food delivery orders utility
  const handleSyncFoodDeliveryOrders = async () => {
    setSyncing(true);
    setLastSyncResult(null);
    try {
      const params = new URLSearchParams();
      if (syncDateFrom) params.append('date_from', syncDateFrom);
      if (syncDateTo) params.append('date_to', syncDateTo);
      params.append('limit', '200');  // Reduced limit for reliability
      
      const res = await axios.post(`${API}/admin/utils/sync-food-delivery-orders?${params.toString()}`, {}, { timeout: 120000 });
      setLastSyncResult(res.data);
      if (res.data.total_synced > 0) {
        toast.success(`Fixed ${res.data.total_synced} records`);
      } else {
        toast.info("All orders in this date range are already in sync");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || "Sync failed - please try again";
      toast.error(errorMsg);
      setLastSyncResult({ error: errorMsg });
    } finally {
      setSyncing(false);
    }
  };

  // Fix missing ledger entries
  const handleFixLedgerEntries = async () => {
    setFixingLedger(true);
    setLedgerFixResult(null);
    try {
      const params = new URLSearchParams();
      if (syncDateFrom) params.append('date_from', syncDateFrom);
      if (syncDateTo) params.append('date_to', syncDateTo);
      params.append('limit', '100');
      
      const res = await axios.post(`${API}/admin/utils/fix-missing-ledger-entries?${params.toString()}`, {}, { timeout: 120000 });
      setLedgerFixResult(res.data);
      if (res.data.ledger_entries_created > 0) {
        toast.success(`Created ${res.data.ledger_entries_created} ledger entries - Rider earnings restored!`);
      } else {
        toast.info("No missing ledger entries found");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || "Fix failed - please try again";
      toast.error(errorMsg);
      setLedgerFixResult({ error: errorMsg });
    } finally {
      setFixingLedger(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">App Store URLs</h2>
        <p className="text-muted-foreground">
          Configure download links for Rider and Customer apps. These URLs are shown to web users.
        </p>
      </div>

      {/* Rider App URLs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bike className="w-5 h-5 text-yellow-500" />
            Rider App Download Links
          </CardTitle>
          <CardDescription>
            These URLs are used in the Rider Web App download banner and the "Join as Rider" link on the landing page.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Rider Play Store URL */}
          <div className="space-y-2">
            <Label htmlFor="playstore-url" className="flex items-center gap-2">
              <PlayCircle className="w-4 h-4 text-green-500" />
              Google Play Store URL (Rider App)
            </Label>
            <Input
              id="playstore-url"
              placeholder="https://play.google.com/store/apps/details?id=com.bizilgo.rider"
              value={appUrls.playstore_url}
              onChange={(e) => setAppUrls({ ...appUrls, playstore_url: e.target.value })}
              data-testid="playstore-url-input"
            />
            {appUrls.playstore_url && (
              <a 
                href={appUrls.playstore_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                Test Link
              </a>
            )}
          </div>

          {/* Rider App Store URL */}
          <div className="space-y-2">
            <Label htmlFor="appstore-url" className="flex items-center gap-2">
              <Apple className="w-4 h-4 text-gray-600" />
              Apple App Store URL (Rider App)
            </Label>
            <Input
              id="appstore-url"
              placeholder="https://apps.apple.com/app/bizilgo-rider/id123456789"
              value={appUrls.appstore_url}
              onChange={(e) => setAppUrls({ ...appUrls, appstore_url: e.target.value })}
              data-testid="appstore-url-input"
            />
            {appUrls.appstore_url && (
              <a 
                href={appUrls.appstore_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                Test Link
              </a>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Customer App URLs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-500" />
            Customer App Download Links
          </CardTitle>
          <CardDescription>
            Enter the App Store and Play Store URLs for the BizilGO Customer PWA.
            These can be used for customer app download prompts (future feature).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Customer Play Store URL */}
          <div className="space-y-2">
            <Label htmlFor="customer-playstore-url" className="flex items-center gap-2">
              <PlayCircle className="w-4 h-4 text-green-500" />
              Google Play Store URL (Customer App)
            </Label>
            <Input
              id="customer-playstore-url"
              placeholder="https://play.google.com/store/apps/details?id=com.bizilgo.customer"
              value={appUrls.customer_playstore_url}
              onChange={(e) => setAppUrls({ ...appUrls, customer_playstore_url: e.target.value })}
              data-testid="customer-playstore-url-input"
            />
            {appUrls.customer_playstore_url && (
              <a 
                href={appUrls.customer_playstore_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                Test Link
              </a>
            )}
          </div>

          {/* Customer App Store URL */}
          <div className="space-y-2">
            <Label htmlFor="customer-appstore-url" className="flex items-center gap-2">
              <Apple className="w-4 h-4 text-gray-600" />
              Apple App Store URL (Customer App)
            </Label>
            <Input
              id="customer-appstore-url"
              placeholder="https://apps.apple.com/app/bizilgo/id123456789"
              value={appUrls.customer_appstore_url}
              onChange={(e) => setAppUrls({ ...appUrls, customer_appstore_url: e.target.value })}
              data-testid="customer-appstore-url-input"
            />
            {appUrls.customer_appstore_url && (
              <a 
                href={appUrls.customer_appstore_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                Test Link
              </a>
            )}
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-app-urls-btn">
        {saving ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            Saving...
          </>
        ) : (
          <>
            <Save className="w-4 h-4 mr-2" />
            Save All App URLs
          </>
        )}
      </Button>

      {/* App Version Settings */}
      <div className="pt-6 border-t">
        <h2 className="text-2xl font-bold text-foreground">App Version Control</h2>
        <p className="text-muted-foreground">
          Set minimum required app versions. Users with older versions will be prompted to update.
        </p>
      </div>

      {/* Rider App Version */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bike className="w-5 h-5 text-yellow-500" />
            Rider App Version
          </CardTitle>
          <CardDescription>
            Set the minimum required version for the Rider App. Users with older versions will see an update prompt.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="rider-version">Version (e.g., 1.0.9)</Label>
              <Input
                id="rider-version"
                placeholder="1.0.9"
                value={appVersions.rider_app_version}
                onChange={(e) => setAppVersions({ ...appVersions, rider_app_version: e.target.value })}
                data-testid="rider-version-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rider-build">Build Number (e.g., 20)</Label>
              <Input
                id="rider-build"
                placeholder="20"
                value={appVersions.rider_app_build_number}
                onChange={(e) => setAppVersions({ ...appVersions, rider_app_build_number: e.target.value })}
                data-testid="rider-build-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="show-popup-rider"
              checked={appVersions.show_update_popup_rider}
              onChange={(e) => setAppVersions({ ...appVersions, show_update_popup_rider: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300"
            />
            <Label htmlFor="show-popup-rider" className="text-sm">
              Show update popup to riders
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="force-update-rider"
              checked={appVersions.force_update_rider}
              onChange={(e) => setAppVersions({ ...appVersions, force_update_rider: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300"
              disabled={!appVersions.show_update_popup_rider}
            />
            <Label htmlFor="force-update-rider" className={`text-sm ${!appVersions.show_update_popup_rider ? 'text-muted-foreground' : ''}`}>
              Force update (users cannot skip)
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* Customer App Version */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-500" />
            Customer App Version
          </CardTitle>
          <CardDescription>
            Set the minimum required version for the Customer App (if applicable).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="customer-version">Version (e.g., 1.0.5)</Label>
              <Input
                id="customer-version"
                placeholder="1.0.5"
                value={appVersions.customer_app_version}
                onChange={(e) => setAppVersions({ ...appVersions, customer_app_version: e.target.value })}
                data-testid="customer-version-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="customer-build">Build Number (e.g., 10)</Label>
              <Input
                id="customer-build"
                placeholder="10"
                value={appVersions.customer_app_build_number}
                onChange={(e) => setAppVersions({ ...appVersions, customer_app_build_number: e.target.value })}
                data-testid="customer-build-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="show-popup-customer"
              checked={appVersions.show_update_popup_customer}
              onChange={(e) => setAppVersions({ ...appVersions, show_update_popup_customer: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300"
            />
            <Label htmlFor="show-popup-customer" className="text-sm">
              Show update popup to customers
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="force-update-customer"
              checked={appVersions.force_update_customer}
              onChange={(e) => setAppVersions({ ...appVersions, force_update_customer: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300"
              disabled={!appVersions.show_update_popup_customer}
            />
            <Label htmlFor="force-update-customer" className={`text-sm ${!appVersions.show_update_popup_customer ? 'text-muted-foreground' : ''}`}>
              Force update (users cannot skip)
            </Label>
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSaveVersions} disabled={saving} className="w-full" data-testid="save-versions-btn">
        {saving ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            Saving...
          </>
        ) : (
          <>
            <RefreshCw className="w-4 h-4 mr-2" />
            Save Version Settings
          </>
        )}
      </Button>

      {/* Ad Banner Settings */}
      <div className="pt-6 border-t">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Ad Banners</h2>
            <p className="text-muted-foreground">
              Configure promotional banners shown to customers while waiting for ride confirmation.
            </p>
          </div>
          <Button onClick={addNewBanner} variant="outline" size="sm">
            <Plus className="w-4 h-4 mr-1" />
            Add Banner
          </Button>
        </div>
      </div>

      {/* Banner List */}
      <div className="space-y-3">
        {adBanners.map((banner, index) => (
          <Card key={banner.id} className={`${!banner.enabled ? 'opacity-60' : ''}`}>
            <CardHeader 
              className="cursor-pointer py-3"
              onClick={() => setExpandedBanner(expandedBanner === banner.id ? null : banner.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                    style={{ 
                      backgroundImage: `url(${getBannerBackground(banner)})`,
                      backgroundSize: 'cover'
                    }}
                  >
                    {index + 1}
                  </div>
                  <div>
                    <CardTitle className="text-sm">{banner.title || `Banner ${index + 1}`}</CardTitle>
                    <CardDescription className="text-xs">{banner.headline}</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={banner.enabled}
                    onChange={(e) => {
                      e.stopPropagation();
                      updateBanner(banner.id, "enabled", e.target.checked);
                    }}
                    className="w-4 h-4 rounded"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeBanner(banner.id);
                    }}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  {expandedBanner === banner.id ? (
                    <ChevronUp className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
              </div>
            </CardHeader>
            
            {expandedBanner === banner.id && (
              <CardContent className="pt-0 space-y-4">
                {/* Redirect URL */}
                <div className="space-y-2">
                  <Label className="text-xs">Redirect URL</Label>
                  <Input
                    placeholder="https://example.com"
                    value={banner.url}
                    onChange={(e) => updateBanner(banner.id, "url", e.target.value)}
                    className="h-9"
                  />
                </div>

                {/* Theme Selection */}
                <div className="space-y-2">
                  <Label className="text-xs">Background Theme</Label>
                  <div className="flex flex-wrap gap-2">
                    {THEME_OPTIONS.map((theme) => (
                      <button
                        key={theme.id}
                        onClick={() => updateBanner(banner.id, "theme", theme.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                          banner.theme === theme.id
                            ? 'border-primary bg-primary text-white'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: theme.color }} />
                        {theme.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Background URL */}
                {banner.theme === "custom" && (
                  <div className="space-y-2">
                    <Label className="text-xs">Custom Background URL</Label>
                    <Input
                      placeholder="https://example.com/image.png"
                      value={banner.background || ""}
                      onChange={(e) => updateBanner(banner.id, "background", e.target.value)}
                      className="h-9"
                    />
                  </div>
                )}

                {/* Text on Image */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Headline</Label>
                    <Input
                      placeholder="FRESH FROM FARM"
                      value={banner.headline}
                      onChange={(e) => updateBanner(banner.id, "headline", e.target.value)}
                      className="h-9 font-bold"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Subheadline</Label>
                    <Input
                      placeholder="To Your Door"
                      value={banner.subheadline}
                      onChange={(e) => updateBanner(banner.id, "subheadline", e.target.value)}
                      className="h-9"
                    />
                  </div>
                </div>

                {/* Text Color */}
                <div className="flex items-center gap-3">
                  <Label className="text-xs">Text Color:</Label>
                  <input
                    type="color"
                    value={banner.text_color}
                    onChange={(e) => updateBanner(banner.id, "text_color", e.target.value)}
                    className="w-8 h-8 rounded cursor-pointer"
                  />
                  <button
                    onClick={() => updateBanner(banner.id, "text_color", "#ffffff")}
                    className="px-2 py-1 text-xs border rounded hover:bg-muted"
                  >
                    White
                  </button>
                  <button
                    onClick={() => updateBanner(banner.id, "text_color", "#000000")}
                    className="px-2 py-1 text-xs border rounded hover:bg-muted"
                  >
                    Black
                  </button>
                </div>

                {/* Card Info */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Card Title</Label>
                    <Input
                      placeholder="Brand Name"
                      value={banner.title}
                      onChange={(e) => updateBanner(banner.id, "title", e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Description</Label>
                    <Input
                      placeholder="Short description"
                      value={banner.description}
                      onChange={(e) => updateBanner(banner.id, "description", e.target.value)}
                      className="h-9"
                    />
                  </div>
                </div>

                {/* Preview */}
                <div className="p-3 bg-muted rounded-lg">
                  <Label className="text-xs text-muted-foreground mb-2 block">Preview</Label>
                  <div className="bg-white rounded-xl overflow-hidden shadow-sm border max-w-xs">
                    <div 
                      className="relative h-24 flex items-center justify-center"
                      style={{
                        backgroundImage: `url(${getBannerBackground(banner)})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    >
                      <span className="absolute top-1.5 left-1.5 bg-black/60 text-white text-[8px] px-1.5 py-0.5 rounded">Ad</span>
                      <div className="text-center px-3" style={{ color: banner.text_color, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                        <p className="text-sm font-bold tracking-wide">{banner.headline || "HEADLINE"}</p>
                        <p className="text-[10px] font-semibold">{banner.subheadline || "Subheadline"}</p>
                      </div>
                    </div>
                    <div className="p-2 flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-xs text-gray-900">{banner.title || "Title"}</p>
                        <p className="text-[10px] text-gray-500">{banner.description || "Description"}</p>
                      </div>
                      <span className="text-[10px] text-blue-600 font-medium">Learn More →</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      <Button onClick={handleSaveAdBanners} disabled={saving} className="w-full" data-testid="save-ad-banners-btn">
        {saving ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            Saving...
          </>
        ) : (
          <>
            <Megaphone className="w-4 h-4 mr-2" />
            Save All Banners ({adBanners.length})
          </>
        )}
      </Button>

      {/* Ad Banner Analytics */}
      <Card className="mt-6">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Banner Analytics
              </CardTitle>
              <CardDescription>Last 30 days performance</CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchBannerAnalytics}
              disabled={analyticsLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${analyticsLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {analyticsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : bannerAnalytics ? (
            <div className="space-y-4">
              {/* Totals Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{bannerAnalytics.totals?.impressions?.toLocaleString() || 0}</p>
                  <p className="text-xs text-blue-600">Impressions</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{bannerAnalytics.totals?.clicks?.toLocaleString() || 0}</p>
                  <p className="text-xs text-green-600">Clicks</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-purple-700">{bannerAnalytics.totals?.ctr || 0}%</p>
                  <p className="text-xs text-purple-600">CTR</p>
                </div>
              </div>

              {/* Per-Banner Stats Table */}
              {bannerAnalytics.banners && bannerAnalytics.banners.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left py-2 px-3 font-medium">Banner</th>
                        <th className="text-right py-2 px-3 font-medium">Impressions</th>
                        <th className="text-right py-2 px-3 font-medium">Clicks</th>
                        <th className="text-right py-2 px-3 font-medium">CTR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bannerAnalytics.banners.map((b, idx) => (
                        <tr key={b.banner_id || idx} className="border-t">
                          <td className="py-2 px-3 font-medium">{b.title || b.banner_id}</td>
                          <td className="py-2 px-3 text-right text-muted-foreground">{b.impressions?.toLocaleString()}</td>
                          <td className="py-2 px-3 text-right text-muted-foreground">{b.clicks?.toLocaleString()}</td>
                          <td className="py-2 px-3 text-right">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              b.ctr >= 5 ? 'bg-green-100 text-green-700' : 
                              b.ctr >= 2 ? 'bg-yellow-100 text-yellow-700' : 
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {b.ctr}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4 text-sm">
                  No analytics data yet. Data will appear once customers view banners.
                </p>
              )}

              {/* City-wise Breakdown */}
              {bannerAnalytics.cities && bannerAnalytics.cities.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    City-wise Breakdown
                  </h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-orange-50">
                        <tr>
                          <th className="text-left py-2 px-3 font-medium">City</th>
                          <th className="text-right py-2 px-3 font-medium">Impressions</th>
                          <th className="text-right py-2 px-3 font-medium">Clicks</th>
                          <th className="text-right py-2 px-3 font-medium">CTR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bannerAnalytics.cities.map((c, idx) => (
                          <tr key={c.city || idx} className="border-t">
                            <td className="py-2 px-3 font-medium">{c.city || 'Unknown'}</td>
                            <td className="py-2 px-3 text-right text-muted-foreground">{c.impressions?.toLocaleString()}</td>
                            <td className="py-2 px-3 text-right text-muted-foreground">{c.clicks?.toLocaleString()}</td>
                            <td className="py-2 px-3 text-right">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                c.ctr >= 5 ? 'bg-green-100 text-green-700' : 
                                c.ctr >= 2 ? 'bg-yellow-100 text-yellow-700' : 
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {c.ctr}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <p className="text-xs text-muted-foreground text-center mt-4">
                Data from {bannerAnalytics.start_date} to {bannerAnalytics.end_date} • Retained for 90 days
              </p>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4 text-sm">
              No analytics data available.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Data Sync Utilities */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-amber-500" />
            Data Sync Utilities
          </CardTitle>
          <CardDescription>
            Fix data inconsistencies between collections. Use when you notice orders showing wrong status.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-muted rounded-lg space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h4 className="font-medium text-foreground flex items-center gap-2">
                  <Utensils className="w-4 h-4 text-orange-500" />
                  Sync Food Delivery Orders
                </h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Fixes orders showing wrong status. Select a date range to process specific orders.
                </p>
              </div>
            </div>
            
            {/* Date Range Selector */}
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[140px]">
                <Label className="text-xs text-muted-foreground">From Date</Label>
                <Input
                  type="date"
                  value={syncDateFrom}
                  onChange={(e) => setSyncDateFrom(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="flex-1 min-w-[140px]">
                <Label className="text-xs text-muted-foreground">To Date</Label>
                <Input
                  type="date"
                  value={syncDateTo}
                  onChange={(e) => setSyncDateTo(e.target.value)}
                  className="mt-1"
                />
              </div>
              <Button 
                onClick={handleSyncFoodDeliveryOrders}
                disabled={syncing}
                variant="outline"
                className="shrink-0"
                data-testid="sync-food-orders-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? "Syncing..." : "Run Sync"}
              </Button>
            </div>
            
            {lastSyncResult && !lastSyncResult.error && (
              <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-medium text-sm">Sync Complete - Fixed {lastSyncResult.total_synced || 0} records</span>
                </div>
                <p className="text-xs mt-1 text-muted-foreground">
                  {lastSyncResult.date_range && <span>Range: {lastSyncResult.date_range}</span>}
                </p>
                {lastSyncResult.details && (
                  <div className="text-xs mt-2 space-y-1">
                    <p>• Delivered status fixed: <strong>{lastSyncResult.details.delivered_status_fixed || 0}</strong></p>
                    <p>• Cancelled status fixed: <strong>{lastSyncResult.details.cancelled_status_fixed || 0}</strong></p>
                    <p>• Assigned status fixed: <strong>{lastSyncResult.details.assigned_status_fixed || 0}</strong></p>
                    <p>• Rides→QC synced: <strong>{lastSyncResult.details.rides_to_qc_synced || 0}</strong></p>
                    <p>• QC→Rides synced: <strong>{lastSyncResult.details.qc_to_rides_synced || 0}</strong></p>
                  </div>
                )}
              </div>
            )}
            
            {lastSyncResult?.error && (
              <div className="p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-600 dark:text-red-400">{lastSyncResult.error}</p>
              </div>
            )}
            
            <p className="text-xs text-muted-foreground">
              Tip: Start with a small date range (1-3 days) and expand if needed.
            </p>
          </div>
          
          {/* Fix Missing Ledger Entries - for rider earnings */}
          <div className="p-4 bg-muted rounded-lg space-y-4 mt-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h4 className="font-medium text-foreground flex items-center gap-2">
                  <Utensils className="w-4 h-4 text-green-500" />
                  Fix Missing Rider Earnings
                </h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Creates missing ledger entries for completed orders. Use this if rider earnings show ₹0 for delivered orders.
                </p>
              </div>
              <Button 
                onClick={handleFixLedgerEntries}
                disabled={fixingLedger}
                variant="outline"
                className="shrink-0"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${fixingLedger ? 'animate-spin' : ''}`} />
                {fixingLedger ? "Fixing..." : "Fix Earnings"}
              </Button>
            </div>
            
            {ledgerFixResult && !ledgerFixResult.error && (
              <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-medium text-sm">Created {ledgerFixResult.ledger_entries_created} ledger entries</span>
                </div>
                <p className="text-xs mt-1 text-muted-foreground">
                  Range: {ledgerFixResult.date_range} | Orders checked: {ledgerFixResult.total_orders_checked} | Skipped: {ledgerFixResult.skipped_already_exists}
                </p>
              </div>
            )}
            
            {ledgerFixResult?.error && (
              <div className="p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-600 dark:text-red-400">{ledgerFixResult.error}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AppSettingsPage;
